<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { ref, type Ref } from "vue";
import { ApiRequestError } from "../api/http.js";
import type { TenantSummary } from "../api/tenants.js";
import type { ServiceTier } from "../api/system-tiers.js";
import { useAuthenticationStore } from "../stores/authentication.js";
import { useTenantStore } from "../stores/tenants.js";
import { suggestedTenantSlug } from "../tenants/tenant-slug.js";

const emit: (event: "created", tenant: TenantSummary) => void = defineEmits<{
  created: [tenant: TenantSummary];
}>();
const properties: { readonly tiers: readonly ServiceTier[] } = defineProps<{
  readonly tiers: readonly ServiceTier[];
}>();
const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const tenantStore: ReturnType<typeof useTenantStore> = useTenantStore();
const displayName: Ref<string> = ref("");
const slug: Ref<string> = ref("");
const slugIsManuallyEdited: Ref<boolean> = ref(false);
const tierId: Ref<string> = ref("");
const submitting: Ref<boolean> = ref(false);
const errorMessage: Ref<string | null> = ref(null);

async function submit(): Promise<void> {
  const userId: string | undefined = authenticationStore.session?.user.id;
  if (submitting.value || userId === undefined || tierId.value === "") {
    if (tierId.value === "") {
      errorMessage.value = "Select a tier before creating the tenant.";
    }
    return;
  }

  submitting.value = true;
  errorMessage.value = null;
  try {
    const tenant: TenantSummary = await tenantStore.addTenant(
      { displayName: displayName.value, slug: slug.value },
      tierId.value,
      userId,
    );
    displayName.value = "";
    slug.value = "";
    slugIsManuallyEdited.value = false;
    tierId.value = "";
    emit("created", tenant);
  } catch (error: unknown) {
    errorMessage.value =
      error instanceof ApiRequestError ? error.message : "The tenant could not be created.";
  } finally {
    submitting.value = false;
  }
}

function updateSuggestedSlug(): void {
  if (!slugIsManuallyEdited.value) {
    slug.value = suggestedTenantSlug(displayName.value);
  }
}

function markSlugAsManuallyEdited(): void {
  slugIsManuallyEdited.value = true;
}
</script>

<template>
  <form class="tenant-form" @submit.prevent="submit">
    <label for="tenant-display-name">Display name</label>
    <input
      id="tenant-display-name"
      v-model="displayName"
      required
      maxlength="120"
      autocomplete="off"
      @input="updateSuggestedSlug"
    />
    <label for="tenant-slug">Slug</label>
    <input
      id="tenant-slug"
      v-model="slug"
      required
      maxlength="63"
      pattern="[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*"
      placeholder="example-tenant"
      autocomplete="off"
      @input="markSlugAsManuallyEdited"
    />
    <label for="tenant-tier">Tier</label>
    <select id="tenant-tier" v-model="tierId" required>
      <option disabled value="">Select a tier</option>
      <option v-for="tier in properties.tiers" :key="tier.id" :value="tier.id">{{ tier.name }}</option>
    </select>
    <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>
    <button class="button button--primary" type="submit" :disabled="submitting || properties.tiers.length === 0">
      <span>{{ submitting ? "Creating…" : "Create tenant" }}</span>
      <span aria-hidden="true">→</span>
    </button>
  </form>
</template>
